import { useEffect, useState } from 'react';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, NumberInput, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import { useTenantMap } from '../../../../data/useTenants';
import { formatMinor } from '../../../../lib/format';
import { validationLabel } from './InvoicesTable';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * Local calendar parts, not `toISOString()`.
 *
 * `new Date(y, m, 1).toISOString()` serialises a local midnight as UTC, so in
 * Africa/Johannesburg (the platform default) the "first of the month" default
 * came out as the last day of the previous month.
 */
function isoDate(d: Date): string {
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function firstOfMonth(): string {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function lastOfMonth(): string {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onGenerated: () => void;
}

export function GenerateInvoiceDrawer({ opened, onClose, onGenerated }: Props) {
  const t = useT();
  const { map: tenants } = useTenantMap();
  const [orgId, setOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [country, setCountry] = useState('ZA');
  const [periodStart, setPeriodStart] = useState(firstOfMonth());
  const [periodEnd, setPeriodEnd] = useState(lastOfMonth());
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number | ''>(99900);
  const [issue, setIssue] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ org?: string; amount?: string }>({});

  // Every field is local state, so without this the next invoice inherits the
  // previous organisation, VAT number and amount: a real risk of billing the
  // wrong tenant.
  useEffect(() => {
    if (opened) return;
    setOrgId(''); setOrgName(''); setVatNumber(''); setCountry('ZA');
    setPeriodStart(firstOfMonth()); setPeriodEnd(lastOfMonth());
    setDescription(''); setAmount(99900); setIssue(true); setErrors({});
  }, [opened]);

  const tenantOptions = Array.from(tenants.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({ value: o.id, label: o.name }));

  function chooseOrg(id: string) {
    setOrgId(id);
    setErrors((e) => ({ ...e, org: undefined }));
    // The name is what the PDF prints, so it follows the choice unless the
    // operator has already typed something of their own.
    const tenant = tenants.get(id);
    if (tenant && !orgName) setOrgName(tenant.name);
  }

  async function generate() {
    const next: { org?: string; amount?: string } = {};
    if (!orgId) next.org = t('billing.invoices.form.errors.orgRequired');
    if (typeof amount !== 'number' || amount <= 0 || !Number.isInteger(amount)) {
      next.amount = t('billing.invoices.form.errors.amountPositive');
    }
    setErrors(next);
    if (next.org || next.amount) return;

    setBusy(true);
    try {
      const res = await admin.generateInvoice({
        organisation_id: orgId,
        org_name: orgName || null,
        vat_number: vatNumber || null,
        country_iso: country,
        period_start: periodStart,
        period_end: periodEnd,
        issue,
        lines: [{
          description: description || t('billing.invoices.form.lineDescriptionDefault'),
          quantity: 1,
          unit_amount_minor: amount as number
        }]
      });
      const d = res.data;
      notifySuccess(
        t('billing.invoices.toast.generated', {
          number: d.invoice_number,
          amount: formatMinor(d.total_minor, d.currency),
          vatLabel: d.vat_label,
          vatPercent: d.vat_percent,
          validation: validationLabel(t, d.validation_status)
        }),
        t('billing.invoices.toast.generatedTitle')
      );
      onGenerated();
      onClose();
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.invoices.toast.generateFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={t('billing.invoices.form.title')} position="right" size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('billing.invoices.form.vatHint')}
        </Text>
        <Select
          label={t('billing.invoices.form.organisation')}
          value={orgId}
          onChange={(e) => chooseOrg(e.currentTarget.value)}
          error={errors.org}
          required
          data={[{ value: '', label: t('billing.invoices.form.orgPlaceholder') }, ...tenantOptions]}
        />
        <TextInput label={t('billing.invoices.form.orgName')} value={orgName} onChange={(e) => setOrgName(e.currentTarget.value)} description={t('billing.invoices.form.orgNamePlaceholder')} />
        <TextInput label={t('billing.invoices.form.vatNumber')} value={vatNumber} onChange={(e) => setVatNumber(e.currentTarget.value)} placeholder={t('common.optional')} />
        <Select
          label={t('billing.invoices.form.country')}
          value={country}
          onChange={(e) => setCountry(e.currentTarget.value)}
          data={[
            { value: 'ZA', label: t('billing.invoices.form.country.za') },
            { value: 'NG', label: t('billing.invoices.form.country.ng') },
            { value: 'KE', label: t('billing.invoices.form.country.ke') }
          ]}
        />
        <Group gap="md" grow>
          {/* Native date inputs: the period bounds are `DateOnly` server-side and
              a malformed string used to reach the API untouched. */}
          <TextInput type="date" label={t('billing.invoices.form.periodStart')} value={periodStart} onChange={(e) => setPeriodStart(e.currentTarget.value)} />
          <TextInput type="date" label={t('billing.invoices.form.periodEnd')} value={periodEnd} onChange={(e) => setPeriodEnd(e.currentTarget.value)} />
        </Group>
        <TextInput
          label={t('billing.invoices.form.lineDescription')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          placeholder={t('billing.invoices.form.lineDescriptionDefault')}
        />
        <NumberInput
          label={t('billing.invoices.form.amount')}
          value={amount}
          onChange={(v) => { setAmount(v); setErrors((e) => ({ ...e, amount: undefined })); }}
          min={1}
          error={errors.amount}
          description={t('billing.invoices.form.amountHint')}
        />
        <Switch label={t('billing.invoices.form.issueNow')} description={t('billing.invoices.form.issueNowHint')} checked={issue} onChange={(next) => setIssue(next)} />
        <Group justify="flex-end" gap="sm" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={generate} loading={busy}>{t('billing.invoices.form.submit')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
